/**
 * Flow loader for the Flow Engine
 * Loads and validates flow JSON files with React environment adaptations
 */

import { logger } from '../../utils/logger';

import type { Flow, FlowLoadOptions, Step } from './types';
import { FlowError } from './types';

// Create component logger for flow loader
const flowLoaderLogger = logger.withComponent('FlowLoader');

/**
 * Utility class for loading and validating flow definitions
 * 
 * Supports:
 * - Dynamic import of flow JSON files
 * - Schema validation (v1.0.0 to v1.2.0)
 * - Duplicate step ID detection
 * - Input visibility validation
 * - CDN and local file loading
 * 
 * @example
 * ```typescript
 * // Load a flow
 * const flow = await FlowLoader.loadFlow('wifi-help');
 * 
 * // Load from CDN
 * const flow = await FlowLoader.loadFlowFromUrl('https://cdn.example.com/flows/wifi-help.json');
 * ```
 */
export class FlowLoader {
  private static cache = new Map<string, Flow>();
  private static defaultOptions: Required<FlowLoadOptions> = {
    baseUrl: '',
    enableCache: true,
    validation: 'strict',
  };

  /**
   * Load a flow by ID from the local flows directory
   * 
   * @param flowId - Unique flow identifier
   * @param options - Loading configuration options
   * @returns Promise resolving to validated flow
   */
  static async loadFlow(flowId: string, options: Partial<FlowLoadOptions> = {}): Promise<Flow> {
    const config = { ...this.defaultOptions, ...options };

    // Check cache first
    if (config.enableCache && this.cache.has(flowId)) {
      return this.cache.get(flowId)!;
    }

    try {
      // Dynamic import of the flow JSON file
      // This assumes flows are stored in src/flows/authored/ directory
      const flowModule = await import(`../../flows/authored/${flowId}.json`);
      const flow = flowModule.default || flowModule;

      // Validate the flow structure
      if (config.validation !== 'none') {
        this.validateFlow(flow, config.validation === 'strict');
      }

      // Cache the validated flow
      if (config.enableCache) {
        this.cache.set(flowId, flow);
      }

      return flow;
    } catch (error) {
      if (error instanceof FlowError) {
        throw error;
      }
      throw new FlowError('loadError', `Failed to load flow: ${flowId}`, error);
    }
  }

  /**
   * Load a flow from a URL (for CDN or remote loading)
   * 
   * @param url - Full URL to flow JSON file
   * @param options - Loading configuration options
   * @returns Promise resolving to validated flow
   */
  static async loadFlowFromUrl(url: string, options: Partial<FlowLoadOptions> = {}): Promise<Flow> {
    const config = { ...this.defaultOptions, ...options };

    // Check cache using URL as key
    if (config.enableCache && this.cache.has(url)) {
      return this.cache.get(url)!;
    }

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const flow = await response.json();

      // Validate the flow structure
      if (config.validation !== 'none') {
        this.validateFlow(flow, config.validation === 'strict');
      }

      // Cache the validated flow
      if (config.enableCache) {
        this.cache.set(url, flow);
      }

      return flow;
    } catch (error) {
      if (error instanceof FlowError) {
        throw error;
      }
      throw new FlowError('loadError', `Failed to load flow from URL: ${url}`, error);
    }
  }

  /**
   * Load a flow from a JSON object (for testing or dynamic flows)
   * 
   * @param flowData - Flow definition object
   * @param options - Loading configuration options
   * @returns Promise resolving to validated flow
   */
  static async loadFlowFromObject(flowData: any, options: Partial<FlowLoadOptions> = {}): Promise<Flow> {
    const config = { ...this.defaultOptions, ...options };

    try {
      // Validate the flow structure
      if (config.validation !== 'none') {
        this.validateFlow(flowData, config.validation === 'strict');
      }

      return flowData as Flow;
    } catch (error) {
      if (error instanceof FlowError) {
        throw error;
      }
      throw new FlowError('loadError', 'Failed to validate flow object', error);
    }
  }

  /**
   * Validate flow structure and check for common issues
   * 
   * @private
   * @param flow - Flow object to validate
   * @param strict - Whether to use strict validation
   */
  private static validateFlow(flow: Flow, strict: boolean = true): void {
    // Required fields
    if (!flow.id) {
      throw new FlowError('validation', 'Flow missing required field: id');
    }

    if (!flow.schemaVersion) {
      throw new FlowError('validation', 'Flow missing required field: schemaVersion');
    }

    // Schema version compatibility
    const supportedVersions = ['1.0.0', '1.1.0', '1.2.0'];
    if (strict && !supportedVersions.includes(flow.schemaVersion)) {
      flowLoaderLogger.warn(`Unsupported schema version: ${flow.schemaVersion}. Supported versions: ${supportedVersions.join(', ')}`);
    }

    // Steps validation
    if (!Array.isArray(flow.steps) || flow.steps.length === 0) {
      throw new FlowError('validation', 'Flow must have at least one step');
    }

    // Check for duplicate step IDs
    const stepIds = new Set<string>();
    const checkDuplicateId = (step: Step, source: string) => {
      if (!step.id) {
        throw new FlowError('validation', `Step missing required field 'id' in ${source}`);
      }
      if (stepIds.has(step.id)) {
        throw new FlowError('validation', `Duplicate step ID found: ${step.id}`);
      }
      stepIds.add(step.id);
    };

    // Check main steps
    flow.steps.forEach((step, index) => checkDuplicateId(step, `steps[${index}]`));

    // Check reusable steps
    if (flow.reusable) {
      Object.entries(flow.reusable).forEach(([key, step]) => 
        checkDuplicateId(step, `reusable.${key}`)
      );
    }

    // Validate step references
    if (strict) {
      this.validateStepReferences(flow);
    }

    // Validate input visibility configurations
    this.validateInputVisibility(flow);

    // Additional validations for strict mode
    if (strict) {
      this.validateStepTypes(flow);
      this.validateChipReferences(flow);
    }
  }

  /**
   * Validate that step references point to existing steps
   * 
   * @private
   */
  private static validateStepReferences(flow: Flow): void {
    const allStepIds = new Set([
      ...flow.steps.map(s => s.id),
      ...(flow.reusable ? Object.keys(flow.reusable) : [])
    ]);

    const validateReference = (stepId: string | undefined, fieldName: string, sourceStep: string) => {
      if (stepId && !allStepIds.has(stepId)) {
        flowLoaderLogger.warn(`Invalid step reference in ${sourceStep}.${fieldName}: ${stepId}`);
      }
    };

    // Check next and errorNext references in main steps
    flow.steps.forEach(step => {
      validateReference(step.next, 'next', step.id);
      validateReference(step.errorNext, 'errorNext', step.id);
      
      // Check chip references
      step.chips?.forEach((chip, chipIndex) => {
        validateReference(chip.next, `chips[${chipIndex}].next`, step.id);
      });
    });

    // Check reusable step references
    if (flow.reusable) {
      Object.values(flow.reusable).forEach(step => {
        validateReference(step.next, 'next', step.id);
        validateReference(step.errorNext, 'errorNext', step.id);
        
        step.chips?.forEach((chip, chipIndex) => {
          validateReference(chip.next, `chips[${chipIndex}].next`, step.id);
        });
      });
    }
  }

  /**
   * Validate input visibility configurations
   * 
   * @private
   */
  private static validateInputVisibility(flow: Flow): void {
    const validateStep = (step: Step) => {
      // Warn about potentially problematic configurations
      if (step.type === 'textInput' && step.inputVisibility === 'plus') {
        flowLoaderLogger.warn(
          `Step "${step.id}" requests text input but hides text field. ` +
          `Consider using inputVisibility: "text" or "both"`
        );
      }
      if (step.type === 'imageInput' && step.inputVisibility === 'text') {
        flowLoaderLogger.warn(
          `Step "${step.id}" requests image input but hides attachment button. ` +
          `Consider using inputVisibility: "plus" or "both"`
        );
      }
    };

    // Validate main steps
    flow.steps.forEach(validateStep);

    // Validate reusable steps
    if (flow.reusable) {
      Object.values(flow.reusable).forEach(validateStep);
    }
  }

  /**
   * Validate step types are supported
   * 
   * @private
   */
  private static validateStepTypes(flow: Flow): void {
    const supportedTypes = ['system', 'chips', 'textInput', 'imageInput', 'llm', 'vision', 'imageEdit', 'helpdesk', 'reusable'];
    const unsupportedTypes = ['logic']; // Currently not supported

    const validateStepType = (step: Step) => {
      if (!supportedTypes.includes(step.type)) {
        if (unsupportedTypes.includes(step.type)) {
          flowLoaderLogger.warn(`Step "${step.id}" uses unsupported type: ${step.type}`);
        } else {
          flowLoaderLogger.warn(`Step "${step.id}" uses unknown type: ${step.type}`);
        }
      }
    };

    flow.steps.forEach(validateStepType);
    if (flow.reusable) {
      Object.values(flow.reusable).forEach(validateStepType);
    }
  }

  /**
   * Validate chip configurations
   * 
   * @private
   */
  private static validateChipReferences(flow: Flow): void {
    const validateChips = (step: Step) => {
      if (step.chips) {
        const chipIds = new Set<string>();
        step.chips.forEach((chip, index) => {
          if (!chip.id) {
            flowLoaderLogger.warn(`Chip missing ID in step "${step.id}" at index ${index}`);
          } else if (chipIds.has(chip.id)) {
            flowLoaderLogger.warn(`Duplicate chip ID "${chip.id}" in step "${step.id}"`);
          } else {
            chipIds.add(chip.id);
          }

          if (!chip.label) {
            flowLoaderLogger.warn(`Chip "${chip.id}" missing label in step "${step.id}"`);
          }
        });
      }
    };

    flow.steps.forEach(validateChips);
    if (flow.reusable) {
      Object.values(flow.reusable).forEach(validateChips);
    }
  }

  /**
   * Find a step by ID in the flow (checks both main steps and reusable)
   * 
   * @param flow - Flow to search in
   * @param stepId - ID of step to find
   * @returns Step if found, null otherwise
   */
  static findStep(flow: Flow, stepId: string): Step | null {
    // Check main steps
    const mainStep = flow.steps.find((s) => s.id === stepId);
    if (mainStep) return mainStep;

    // Check reusable steps
    if (flow.reusable && flow.reusable[stepId]) {
      return flow.reusable[stepId];
    }

    return null;
  }

  /**
   * Clear the flow cache
   */
  static clearCache(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  static getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}